import type {PayrixPaymentMeta} from "../@types/payrix";
import type {LawpayPaymentMeta} from "../@types/lawpay";
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const TxType = {
    FundRetainerFromClient: "FR",
    FundRetainerFromOperating: "FO",
    EscrowDeposit: "ED",
    PaymentFromClient: "PC",
    PaymentFromTrust: "PT",
    StartingTrustAmount: "ST",
    StartingAccountAmount: "SA",
    RefundFromOperating: "RO",
    RefundRetainer: "RR",
    DisburseTrust: "DT",
    DisburseEscrow: "DE",
    TrustTransfer: "TT",
    BalanceAdjustment: "BA"
};
export type TxTypeType = typeof TxType[keyof typeof TxType];
export const SourceType = {
    BankAccount: "B",
    Contact: "C",
    Escrow: "E",
    General: "G",
    Matter: "M"
};
export type SourceTypeType = typeof SourceType[keyof typeof SourceType];
export const TargetType = {
    BankAccount: "B",
    Contact: "C",
    Escrow: "E",
    General: "G",
    Matter: "M"
};
export type TargetTypeType = typeof TargetType[keyof typeof TargetType];
export type TransactionPaymentDistribution = {matterId: uuid, amount: string, notes: string};
class Transaction extends Base implements EntityType {
    transactionId: uuid;
    txDate: Date;
    txType: TxTypeType;
    amount: number;
    sourceType?: SourceTypeType | null;
    source?: uuid | null;
    targetType: TargetTypeType;
    target?: uuid | null;
    clientIds: uuid[];
    matterIds: uuid[];
    invoiceId?: uuid | null;
    paymentDistribution: TransactionPaymentDistribution[];
    paymentMethod?: string | null;
    referenceNumber?: string | null;
    processFundsDate?: Date | null;
    fundsAvailableDate?: Date | null;
    refusedDate?: Date | null;
    voidedDate?: Date | null;
    notes?: string | null;
    processor?: string | null;
    processorPaymentId?: string | null;
    processorPaymentMeta?: PayrixPaymentMeta | null;
    relatedToClientTrust: boolean;
    currentBalance?: number | null;
    reconcileNotes?: string | null;
    isReconciled: boolean;
    constructor(doc: typeof Transaction | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            transactionId: genUUID(),
            txDate: null,
            txType: null,
            amount: null,
            targetType: null,
            clientIds: [],
            matterIds: [],
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "transaction";
        // @ts-ignore
        this.transactionId = doc._transactionId || doc.transactionId || doc.transactionid || genUUID();
        // @ts-ignore
        this.txDate = DateUtil.timestampOnly(doc._txDate) || DateUtil.timestampOnly(doc.txDate) || DateUtil.timestampOnly(doc.txdate) || null;
        // @ts-ignore
        this.txType = doc._txType || doc.txType || doc.txtype || null;
        // @ts-ignore
        this.amount = util.coalesce(util.parseFloat(doc._amount), util.parseFloat(doc.amount), util.parseFloat(doc.amount), null);
        // @ts-ignore
        this.sourceType = util.trimRight(doc._sourceType) || util.trimRight(doc.sourceType) || util.trimRight(doc.sourcetype) || null;
        // @ts-ignore
        this.source = doc._source || doc.source || null;
        // @ts-ignore
        this.targetType = util.trimRight(doc._targetType) || util.trimRight(doc.targetType) || util.trimRight(doc.targettype) || null;
        // @ts-ignore
        this.target = doc._target || doc.target || null;
        // @ts-ignore
        this.clientIds = doc._clientIds || doc.clientIds || doc.clientids || [];
        // @ts-ignore
        this.matterIds = doc._matterIds || doc.matterIds || doc.matterids || [];
        // @ts-ignore
        this.invoiceId = doc._invoiceId || doc.invoiceId || doc.invoiceid || null;
        // @ts-ignore
        this.paymentDistribution = doc._paymentDistribution || doc.paymentDistribution || doc.paymentdistribution || [];
        // @ts-ignore
        this.paymentMethod = doc._paymentMethod || doc.paymentMethod || doc.paymentmethod || null;
        // @ts-ignore
        this.referenceNumber = doc._referenceNumber || doc.referenceNumber || doc.referencenumber || null;
        // @ts-ignore
        this.processFundsDate = DateUtil.dateOnly(doc._processFundsDate) || DateUtil.dateOnly(doc.processFundsDate) || DateUtil.dateOnly(doc.processfundsdate) || null;
        // @ts-ignore
        this.fundsAvailableDate = DateUtil.dateOnly(doc._fundsAvailableDate) || DateUtil.dateOnly(doc.fundsAvailableDate) || DateUtil.dateOnly(doc.fundsavailabledate) || null;
        // @ts-ignore
        this.refusedDate = DateUtil.dateOnly(doc._refusedDate) || DateUtil.dateOnly(doc.refusedDate) || DateUtil.dateOnly(doc.refuseddate) || null;
        // @ts-ignore
        this.voidedDate = DateUtil.dateOnly(doc._voidedDate) || DateUtil.dateOnly(doc.voidedDate) || DateUtil.dateOnly(doc.voideddate) || null;
        // @ts-ignore
        this.notes = doc._notes || doc.notes || null;
        // @ts-ignore
        this.processor = doc._processor || doc.processor || null;
        // @ts-ignore
        this.processorPaymentId = doc._processorPaymentId || doc.processorPaymentId || doc.processorpaymentid || null;
        // @ts-ignore
        this.processorPaymentMeta = doc._processorPaymentMeta || doc.processorPaymentMeta || doc.processorpaymentmeta || {};
        // @ts-ignore
        this.relatedToClientTrust = util.defaultBoolean([doc.relatedToClientTrust, doc._relatedToClientTrust, doc.relatedtoclienttrust, false]);
        // @ts-ignore
        this.currentBalance = util.coalesce(util.parseFloat(doc._currentBalance), util.parseFloat(doc.currentBalance), util.parseFloat(doc.currentbalance), null);
        // @ts-ignore
        this.reconcileNotes = doc._reconcileNotes || doc.reconcileNotes || doc.reconcilenotes || null;
        // @ts-ignore
        this.isReconciled = util.defaultBoolean([doc.isReconciled, doc._isReconciled, doc.isreconciled, false]);
    }
    get id(): uuid { return this.transactionId; }
    static TX_TYPE_VALUES = ["FR", "FO", "ED", "PC", "PT", "ST", "SA", "RO", "RR", "DT", "DE", "TT", "BA"];
    static SOURCE_TYPE_VALUES = ["B", "C", "E", "G", "M"];
    static TARGET_TYPE_VALUES = ["B", "C", "E", "G", "M"];
    addClientId(clientId: uuid): uuid {
        if (!clientId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.clientIds) this.clientIds = [];
        this.clientIds.push(clientId);
        return clientId;
    }
    updateClientId(clientId: uuid) {
        if (!this.clientIds) this.clientIds = [];
        const index = this.clientIds.findIndex(item => item === clientId);
        if (index !== -1) { this.clientIds[index] = clientId; }
        else this.addClientId(clientId);
    }
    removeClientId(clientId: uuid) {
        if (!this.clientIds) return;
        let foundItems = this.clientIds?.filter(item => item === clientId);
        if (foundItems && foundItems.length > 0) {
            this.clientIds = this.clientIds.filter(item => !(item === clientId));
        }
    }
    abandonClientIds() { this.clientIds = []; }
    addMatterId(matterId: uuid): uuid {
        if (!matterId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.matterIds) this.matterIds = [];
        this.matterIds.push(matterId);
        return matterId;
    }
    updateMatterId(matterId: uuid) {
        if (!this.matterIds) this.matterIds = [];
        const index = this.matterIds.findIndex(item => item === matterId);
        if (index !== -1) { this.matterIds[index] = matterId; }
        else this.addMatterId(matterId);
    }
    removeMatterId(matterId: uuid) {
        if (!this.matterIds) return;
        let foundItems = this.matterIds?.filter(item => item === matterId);
        if (foundItems && foundItems.length > 0) {
            this.matterIds = this.matterIds.filter(item => !(item === matterId));
        }
    }
    abandonMatterIds() { this.matterIds = []; }
    addPaymentDistribution(paymentDistribution: TransactionPaymentDistribution): TransactionPaymentDistribution {
        if (!paymentDistribution) throw new ValidationError('No nulls allowed in an array!');
        if (!this.paymentDistribution) this.paymentDistribution = [];
        this.paymentDistribution.push(paymentDistribution);
        return paymentDistribution;
    }
    updatePaymentDistribution(paymentDistribution: TransactionPaymentDistribution) {
        if (!this.paymentDistribution) this.paymentDistribution = [];
        const index = this.paymentDistribution.findIndex(item => item === paymentDistribution);
        if (index !== -1) { this.paymentDistribution[index] = paymentDistribution; }
        else this.addPaymentDistribution(paymentDistribution);
    }
    removePaymentDistribution(paymentDistribution: TransactionPaymentDistribution) {
        if (!this.paymentDistribution) return;
        let foundItems = this.paymentDistribution?.filter(item => item === paymentDistribution);
        if (foundItems && foundItems.length > 0) {
            this.paymentDistribution = this.paymentDistribution.filter(item => !(item === paymentDistribution));
        }
    }
    abandonPaymentDistribution() { this.paymentDistribution = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for this transaction"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.transactionId; }
    static override getNonRefArrayFields(): string[] { return ["clientIds", "matterIds", "paymentDistribution"]; }
    static override getDefaultEntries(): Transaction[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'transaction',
    dataArea: 'Trust and Payments',
    attachedTo: [
        {parents: ['contact'], via: 'clientIds', onDelete: 'block', onReplace: 'move'},
        {parents: ['contact'], via: 'sourceType', onDelete: 'block', onReplace: 'move'},
        {parents: ['contact'], via: 'targetType', onDelete: 'block', onReplace: 'move'},
    ],
    table: 'TRANSACTION',
    // Keep deleted rows in the client store and backup (historical references). OWA-68
    includeDeletedInResults: true,
    comment: 'A trust-accounting transaction for a law firm, covering four categories: Payments (funds received from a client or operating account into a trust or operating account), Disbursements (funds paid out from trust to a client or vendor), Transfers (movement of funds between trusts or between trust and operating), and Starters (seed balances when onboarding from another system). Each transaction records a typed source and target (bank account, client general trust, matter trust, or escrow trust), the clients and matters involved, and optionally the payment method, processor details, and reconciliation status.',
    fields: {
        transactionId: {type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for this transaction'},

        /**
         * + ALL
         *
         * Transaction date (we store the timestamp, but really just need the date).
         */
        txDate: {type: 'timestamp', required: true, comment: 'When this transaction was incurred'},

        /**
         * + ALL
         *
         * Transaction types (see sourceType for other codes) [Category]:
         * - FR Fund Retainer: C->E,M, where C is client or responsible payer [Payment]
         * - FO Fund Retainer from Operating: B->G,M, where B is an operating account [Payment]
         * - ED Escrow Deposit: C->E, where C is 3rd party (usually) [Escrow]
         * - PT Payment from Trust: G,M->B, where B is operating [Transfer]
         * - PC Payment from Client to Operating: C->B, where B is operating [Payment]
         * - ST Starting Trust Balance: null->G,M
         * - SA Starting Account Balance: null->M
         * - RO Refund from Operating: B->C where B is Operating and C is client [Withdrawal]
         * - RR Refund Retainer from Trust: G,M->C, where C is client [Withdrawal]
         * - DT Disburse Trust: G,M->C, where C is 3rd party [Withdrawal]
         * - DE Disburse Escrow: E->C [Escrow]
         * - TT Trust Transfer: C,M,E->C,M,E, where C is a client.  There is no E->E or C->C! [Transfer]
         * - BA Balance Adjustment: These happen in-place, there is no movement of funds. [Balance Adjustment]
         */
        txType: {
            type: 'string',
            values: ['FR', 'FO', 'ED', 'PC', 'PT', 'ST', 'SA', 'RO', 'RR', 'DT', 'DE', 'TT', 'BA'],
            valueNames: [
                'Fund Retainer from Client',
                'Fund Retainer from Operating',
                'Escrow Deposit',
                'Payment from Client',
                'Payment from Trust',
                'Starting Trust Amount',
                'Starting Account Amount',
                'Refund from Operating',
                'Refund Retainer',
                'Disburse Trust',
                'Disburse Escrow',
                'Trust Transfer',
                'Balance Adjustment'
            ],
            required: true,
            comment: 'What kind of transaction is this?'
        },

        /**
         * + ALL
         *
         * Transaction amount.
         */
        amount: {type: 'money', required: true, comment: 'How much was the transaction for?'},

        /**
         * + ALL
         *
         * What type of account is the source?  If the transaction starts with a client, it will either be from one
         * of their trusts (client, matter or escrow) or from an external payment method like cash, check or credit card.
         * Values:
         *
         * - [B]ank Account: money is coming from a bank account, usually the operating account.  Source is a bankAccountId.
         * - [C]ontact: money is coming from the client, using a method other than a trust. Source is the client's contactId.
         * - [E]scrow Trust: money is coming from one of the client's escrow trusts. Source is the id of the escrow account.
         *                 Note that escrow accounts are a type of bank account, so this is really a bankAccountId.
         * - [G]eneral Trust: money is coming from the client's general trust
         * - [M]atter Trust: money is coming from one of the client's trusts (general, matter or escrow).
         *                 Source is the id of the matter, if a matter trust, or
         */
        sourceType: {
            type: 'char',
            values: ['B', 'C', 'E', 'G', 'M'],
            valueNames: ['Bank Account', 'Contact', 'Escrow', 'General', 'Matter'],
            entity: {
                map: {B: 'bankAccount', C: 'contact', E: 'escrow', G: 'contact', M: 'matter'},
                value: 'source',
                deleteStrategy: 'preserve' // Keep data for historical purposes
            },
            comment: 'What type of account is the source?'
        },

        /**
         * + ALL
         *
         * This is ID of where the money came from.  See sourceType for a list of origins and the type of ID that will
         * be stored in this field.
         */
        source: {type: 'uuid', comment: 'Source ID for the funds'},

        /**
         * + ALL
         *
         * What type of account is the target?  See sourceType for values.
         *
         * Values: see sourceType, above
         */
        targetType: {
            type: 'char',
            values: ['B', 'C', 'E', 'G', 'M'],
            valueNames: ['Bank Account', 'Contact', 'Escrow', 'General', 'Matter'],
            entity: {
                map: {B: 'bankAccount', C: 'contact', E: 'escrow', G: 'contact', M: 'matter'},
                value: 'target',
                deleteStrategy: 'preserve' // Keep data for historical purposes
            },
            required: true,
            comment: 'What type of account is the target?'
        },

        /**
         * + ALL
         *
         * This is where the source money was moved to.  See targetType for a list of origins and what kind of ID this
         * field will contain.
         */
        target: {type: 'uuid', comment: 'Destination ID for the funds'},

        /**
         * + ALL
         *
         * Clients selected for this transaction.  We can have multiple clients, because cases can have multiple clients.
         */
        clientIds: {type: 'uuid[]', entityType: 'contact', deleteStrategy: 'preserve', required: true, comment: 'Selected client(s)'},

        /**
         * + PAYMENT
         * - DISBURSEMENT, TRANSFER
         *
         * A Payment can be applied to multiple matters, but the matters must all have at least one
         * client in common.
         */
        matterIds: {
            type: 'uuid[]',
            entityType: 'matter',
            deleteStrategy: 'preserve',
            required: true,
            comment: 'Which matters does this transaction involve?'
        },

        /**
         * Which invoice this transaction shows up in.
         */
        invoiceId: {type: 'uuid', entityType: 'invoice', comment: 'Invoice associated with this transaction'},

        /**
         * + PAYMENT
         * - DISBURSEMENT, TRANSFER
         *
         * If a Payment is being applied to multiple invoices, we need to keep track of the distributions.
         *
         * This will be an array with entries like this:
         *
         *   [{ invoiceId: b39a95e2-c907-11e9-a32f-2a2ae2dbcce4, amount: 5000.00, notes: 'Initial deposit' }]
         */
        paymentDistribution: {
            type: 'json[]',
            jsdoc: '{matterId: uuid, amount: string, notes: string}',
            entity: {
                type: 'matter',
                value: 'matterId',
                deleteStrategy: 'preserve' // Must keep all parts of a paymentDistribution, or things stop adding up
            },
            comment: 'If this transaction is split across multiple matters, the amount allocated to each'
        },

        /**
         * + PAYMENT
         * - DISBURSEMENT, TRANSFER
         *
         * For Payments, this is the method of payment. It holds one of two very
         * different things depending on how the payment was taken:
         *
         * - Manually recorded payments store a human descriptor, e.g. cash or check.
         * - Processor payments store the PROCESSOR TOKEN for the payment method used
         *   (Payrix: the 32-character token; LawPay: the method fingerprint). This is
         *   an opaque surrogate, not card data — it is useless without our processor
         *   API credentials, so it is not cardholder data for PCI purposes.
         *
         * This is not required, as disbursements and transfers do not use this.
         */
        paymentMethod: {
            type: 'string',
            comment: 'Method of payment: a descriptor such as cash or check for manual entries, or the processor token for processed payments'
        },

        /**
         * + PAYMENT, DISBURSEMENT
         * - TRANSFER
         *
         * For Payments and Disbursements, a reference number can be provided with the transaction, such as a check number
         * or wire transfer number.  This should NOT be an account number, because we don't want to store financial information.
         * Just in case, we will encrypt this field.
         */
        referenceNumber: {
            type: 'string',
            encrypted: true,
            comment: 'Transaction reference, such as check number or wire transfer ID'
        },

        /**
         * + PAYMENT
         * - DISBURSEMENT, TRANSFER
         *
         * For a Payment, this is the user's best guess as to when the funds will process. When Payrix
         * pays out the funds (in processPayouts.ts), we should update this field with the actual date
         * the transaction was paid out.
         */
        processFundsDate: {type: 'date', comment: 'Date the funds were actually transferred'},

        /**
         * + PAYMENT
         * - DISBURSEMENT, TRANSFER
         *
         * For a Payment, this is the date the funds should clear the bank.  Useful if you have to wait for the money to
         * hit the account before you can use it.
         */
        fundsAvailableDate: {
            type: 'date',
            comment: 'Date the funds should be credited to the account by the financial institution'
        },

        /**
         * + PAYMENT
         * - DISBURSEMENT, TRANSFER
         *
         * If the Payment has been refused, we need to reverse it. And probably add a red flag to the matter.
         * A date here indicates a refusal, null means it has not been refused.
         */
        refusedDate: {type: 'date', comment: 'Date the payment was refused'},

        /**
         * + DISBURSEMENT
         * - PAYMENT, TRANSFER
         *
         * If a Disbursement check needs to be voided, we can do that here.
         * A date here indicates a voided check, null means it has not been voided.
         */
        voidedDate: {type: 'date', comment: 'Date a disbursement check was voided'},

        /**
         * + ALL
         *
         * Notes and comments.  e.g. refusal or voided reasons.
         */
        notes: {type: 'string', required: false, comment: 'Notes from the transaction (public)'},

        /**
         * Which payment processor handled this transaction?
         */
        processor: {type: 'string', comment: 'Payment processor that handled this transaction'},

        /**
         * The payment ID passed to us from the processor, e.g. pmtd_WesqHDqMXBn1RoI6qDTOu for Lawpay
         * or p1_pay_6530d21221c4b339b9fa1e6 for Payrix
         */
        processorPaymentId: {type: 'string', comment: 'The payment ID from the processor'},

        /**
         * Any metadata passed from the processor along with the transaction
         */
        processorPaymentMeta: {
            type: 'json',
            jsdoc: 'PayrixPaymentMeta',
            comment: 'Any metadata passed from the processor along with the transaction'
        },

        /**
         * Used as a flag when showing transactions for a matter that are related to the general trust, but not to the matter directly.
         * We do this so that we can show the running total for the client trust
         */
        relatedToClientTrust: {
            type: 'boolean',
            transient: true,
            comment: `This transaction isn't tied to the matter directly, but to the client's general trust (used to show running trust totals when viewing a matter)`
        },

        /**
         * Used when showing balance calculations
         */
        currentBalance: {type: 'money', transient: true, comment: 'The current balance as of this transaction'},

        /**
         Notes from the bookkeeper on reconciliation
         */
        reconcileNotes: {
            type: 'string', comment: 'Notes from the bookkeeper on reconciliation'
        },

        /**
         Has this transaction been reconciled by the bookkeeper?
         */
        isReconciled: {
            type: 'boolean', default: false, comment: 'Has this transaction been reconciled by the bookkeeper?'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: t => t.status !== 'D'},
        {name: 'trustTypes', fn: t => !['BA', 'SA', 'PC', 'RO'].includes(t.txType) && t.status !== 'D'},
        {name: 'notTrustTypes', fn: t => ['BA', 'SA', 'PC', 'RO'].includes(t.txType) && t.status !== 'D'}
    ],
    maps: [
        {name: 'txType', keysFn: t => t.txType, presentFn: (t, k) => t.txType === k},
        {name: 'invoiceId', keysFn: t => t.invoiceId, presentFn: (t, k) => t.invoiceId === k},
        {
            name: 'matterId',
            // OWA-1711: every Case the transaction names, not only the first, so a Case delete or merge finds it.
            keysFn: t => [...new Set([...(t.matterIds || []), ...(t.sourceType === 'M' ? [t.source] : []), ...(t.targetType === 'M' ? [t.target] : [])])].filter(Boolean),
            presentFn: (t, k) => t.matterIds.includes(k) || t.source === k || t.target === k
        },
        {name: 'clientIds', keysFn: t => t.clientIds, presentFn: (t, k) => t.clientIds?.includes(k)}
    ],
    requiredTypes: ['payrix', 'lawpay']
});
    }
}
export default Transaction;
